import React, { useRef } from 'react';
import { Tenant, RoomMember, Room, BoardingHouseInfo } from '../types';
import Icon from './ui/Icon';
import { formatDateForDisplay } from '../utils/dateUtils';

const IdBoxes: React.FC<{ idNumber: string | undefined }> = ({ idNumber }) => {
    const chars = (idNumber || '').padEnd(12, ' ').split('');
    return (
        <div style={{ display: 'flex', gap: '2px' }}>
            {chars.map((char, index) => (
                <span key={index} style={{ border: '1px solid black', width: '20px', height: '25px', display: 'inline-flex', justifyContent: 'center', alignItems: 'center' }}>
                    {char}
                </span>
            ))}
        </div>
    );
};

const CT01Modal: React.FC<{
    mainPerson: Tenant | RoomMember | null;
    accompanyingPeople: (Tenant | RoomMember)[];
    room: Room | null;
    boardingHouseInfo: BoardingHouseInfo;
    onClose: () => void;
}> = ({ mainPerson, accompanyingPeople, room, boardingHouseInfo, onClose }) => {
    const printRef = useRef<HTMLDivElement>(null);

    // Debug: Log boardingHouseInfo data
    console.log('CT01Modal boardingHouseInfo:', boardingHouseInfo);

    const loadScript = (src: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            const existing = document.querySelector(`script[src="${src}"]`);
            if (existing) {
                existing.addEventListener('load', () => resolve());
                existing.addEventListener('error', () => reject(new Error('Failed to load script')));
                return;
            }
            const s = document.createElement('script');
            s.src = src;
            s.async = true;
            s.onload = () => resolve();
            s.onerror = () => reject(new Error('Failed to load script'));
            document.head.appendChild(s);
        });
    };

    const handleExport = async () => {
        const content = printRef.current;
        if (!content || !mainPerson) return;

        try {
            // Small delay to ensure DOM is fully updated
            await new Promise(r => setTimeout(r, 100));
            
            // Load FileSaver.js for saving files
            if (typeof (window as any).saveAs !== 'function') {
                await loadScript('/vendor/FileSaver.min.js');
                await new Promise(r => setTimeout(r, 50));
            }
            
            // Load html-to-docx from CDN (browser-compatible version)
            if (typeof (window as any).htmlToDocx !== 'function') {
                try {
                    await loadScript('https://unpkg.com/html-to-docx@1.8.0/dist/html-to-docx.umd.js');
                    await new Promise(r => setTimeout(r, 100));
                } catch (e) {
                    throw new Error('Không thể tải thư viện html-to-docx. Vui lòng kiểm tra kết nối internet.');
                }
            }
            
            if (typeof (window as any).htmlToDocx !== 'function') {
                throw new Error('Thư viện html-to-docx không khả dụng sau khi tải.');
            }

            // Debug: Log the HTML content being exported
            console.log('CT01 HTML content for Word export:', content.innerHTML);

            const fileBuffer = await (window as any).htmlToDocx(content.innerHTML, null, {
                orientation: 'portrait',
                margins: { top: 1440, right: 1800, bottom: 1440, left: 1800 }
            });
            (window as any).saveAs(fileBuffer, `CT01_${mainPerson.name.replace(/\s/g, '_')}.docx`);
        } catch (err: any) {
            import('../utils/logger').then(l => l.error('Export CT01 to Word failed', err));
            alert(`Không thể xuất Word: ${err?.message ?? String(err)}. Vui lòng thử lại hoặc kiểm tra kết nối.`);
        }
    };

    const handlePrint = () => {
        const printContent = printRef.current;
        if (printContent) {
            const WinPrint = window.open('', '', 'width=830,height=900');
            WinPrint?.document.write(`
                <html>
                    <head><title>In Tờ khai CT01</title>
                        <style>
                            body { font-family: 'Times New Roman', Times, serif; font-size: 12pt; line-height: 1.4; color: #000; margin: 0; padding: 0; }
                            .page { width: 210mm; min-height: 297mm; padding: 15mm; margin: 0 auto; box-sizing: border-box; }
                            .header, .title { text-align: center; }
                            .header { font-weight: bold; }
                            .title { font-size: 13pt; font-weight: bold; margin: 10px 0; }
                            .info-grid { display: grid; grid-template-columns: auto 1fr; gap: 3px 10px; align-items: center; }
                            .info-grid p { margin: 0; }
                            .table-container { margin-top: 10px; }
                            table { width: 100%; border-collapse: collapse; }
                            th, td { border: 1px solid black; padding: 3px; text-align: center; vertical-align: middle; font-size: 11pt; }
                            .signatures { margin-top: 20px; font-family: 'Times New Roman', Times, serif; }
                            .signature-row { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 10px; margin-bottom: 15px; }
                            .signature-col { text-align: center; }
                            .signature-info { font-size: 11pt; margin: 0; font-family: 'Times New Roman', Times, serif; }
                            .signature-title { font-weight: bold; margin: 0; font-size: 12pt; font-family: 'Times New Roman', Times, serif; }
                            .signature-line { margin: 0; font-size: 11pt; font-family: 'Times New Roman', Times, serif; }
                            .signature-fields { margin-top: 20px; }
                            .id-boxes { display: flex; gap: 1px; }
                            .id-box { border: 1px solid black; width: 18px; height: 22px; display: inline-flex; justify-content: center; align-items: center; font-size: 9pt; }
                        </style>
                    </head>
                    <body><div class="page">${printContent.innerHTML}</div></body>
                </html>
            `);
            WinPrint?.document.close();
            WinPrint?.focus();
            WinPrint?.print();
            WinPrint?.close();
        }
    };

    if (!mainPerson || !room) return null;

    const today = new Date();
    const fullAddress = [boardingHouseInfo.address, boardingHouseInfo.ward, boardingHouseInfo.city].filter(Boolean).join(', ');

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl h-full sm:h-auto sm:max-h-[95vh] flex flex-col">
                <div className="flex-shrink-0 p-4 border-b flex justify-between items-center">
                    <h2 className="text-xl font-semibold">Tờ khai thay đổi thông tin cư trú (CT01)</h2>
                     <div className="flex items-center gap-4">
                       <button onClick={handleExport} className="btn-secondary flex items-center gap-2"><Icon name="word" className="w-5 h-5"/> Tải Word</button>
                       <button onClick={handlePrint} className="btn-secondary flex items-center gap-2"><Icon name="print" className="w-5 h-5"/> In</button>
                       <button onClick={onClose} className="text-gray-500 hover:text-gray-800">&times;</button>
                    </div>
                </div>
                <div className="flex-grow overflow-y-auto p-6" ref={printRef}>
                    <div className="ct01-container printable-document">
                        <div className="header">
                            <p>CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM</p>
                            <p style={{ textDecoration: 'underline' }}>Độc lập – Tự do – Hạnh phúc</p>
                        </div>
                        <h2 className="title">TỜ KHAI THAY ĐỔI THÔNG TIN CƯ TRÚ</h2>
                        <p className="text-center">Kính gửi: {boardingHouseInfo.localPoliceDepartment || '........................'}</p>
                        <br/>
                        <div className="info-grid">
                            <p>1. Họ, chữ đệm và tên:</p><p><strong>{mainPerson.name ? mainPerson.name.toUpperCase() : '........................'}</strong></p>
                            <p>2. Ngày, tháng, năm sinh:</p><p>{formatDateForDisplay(mainPerson.dateOfBirth) || '........................'}</p>
                            <p>3. Giới tính:</p><p>{mainPerson.gender || '........................'}</p>
                            <p>4. Số định danh cá nhân:</p><IdBoxes idNumber={mainPerson.idNumber} />
                            <p>5. Số điện thoại liên hệ:</p><p>{mainPerson.phone || '........................'}</p>
                            <p>6. Email:</p><p>..................................................</p>
                            <p>7. Họ, chữ đệm và tên chủ há:</p><p>{boardingHouseInfo.landlordName || '........................'}</p>
                            <p>8. Mối quan hệ với chủ há:</p><p>Thuê trọ</p>
                            <p>9. Số định danh cá nhân của chủ há:</p><IdBoxes idNumber={boardingHouseInfo.landlordIdNumber} />
                            <p>10. Nội dung đề nghị:</p><p>Đăng ký tạm trú tại địa chỉ: {boardingHouseInfo.name || '...'}, {fullAddress}</p>
                        </div>
                        <div className="table-container">
                            <p>11. Những thành viên trong hộ gia đình cùng thay đổi:</p>
                            <table className="w-full border-collapse border border-black">
                                <thead>
                                    <tr>
                                        <th className="border border-black p-1">TT</th>
                                        <th className="border border-black p-1">Họ, chữ đệm và tên</th>
                                        <th className="border border-black p-1">Ngày, tháng, năm sinh</th>
                                        <th className="border border-black p-1">Giới tính</th>
                                        <th className="border border-black p-1">Số định danh cá nhân</th>
                                        <th className="border border-black p-1">Mối quan hệ với người khai</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {accompanyingPeople.map((person, index) => (
                                        <tr key={person.id}>
                                            <td className="border border-black p-1">{index + 1}</td>
                                            <td className="border border-black p-1">{person.name}</td>
                                            <td className="border border-black p-1">{formatDateForDisplay(person.dateOfBirth)}</td>
                                            <td className="border border-black p-1">{person.gender}</td>
                                            <td className="border border-black p-1">{person.idNumber}</td>
                                            <td className="border border-black p-1">{'relationship' in person ? person.relationship : ''}</td>
                                        </tr>
                                    ))}
                                    {[...Array(Math.max(0, 4 - accompanyingPeople.length))].map((_, i) => (
                                        <tr key={`empty-${i}`}><td className="border border-black p-1 h-10">{accompanyingPeople.length + i + 1}</td><td className="border border-black p-1"></td><td className="border border-black p-1"></td><td className="border border-black p-1"></td><td className="border border-black p-1"></td><td className="border border-black p-1"></td></tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="signatures">
                            <div className="signature-row">
                                <div className="signature-col">
                                    <p className="signature-info">.....,ngày.......tháng.......n.......</p>
                                    <p className="signature-title">Ý KIẾN CỦA CHỦ HỘ(3)</p>
                                </div>
                                <div className="signature-col">
                                    <p className="signature-info">.....,ngày.....tháng.......n...</p>
                                    <p className="signature-title">Ý KIẾN CỦA CHỦ SỞ HỮU CHỖ Ở HỢP PHÁP(4)</p>
                                </div>
                                <div className="signature-col">
                                    <p className="signature-info">.....,ngày......tháng.......n...</p>
                                    <p className="signature-title">Ý KIẾN CỦA CHA HOẶC  MẸ HOẶC NGƯỜI GIÁM HỘ(5)</p>
                                </div>
                                <div className="signature-col">
                                    <p className="signature-info">.....,ngày....tháng.......n...</p>
                                    <p className="signature-title">NGƯỜI KÊ KHAI(6)</p>
                                </div>
                            </div>
                            
                            <div className="signature-fields">
                                <div className="signature-row">
                                    <div className="signature-col">
                                        <p className="signature-line">(7) Há và tên: ..................</p>
                                        <p className="signature-line">(7) Sá dinh danh cá nhân:................</p>
                                    </div>
                                    <div className="signature-col">
                                        <p className="signature-line">(7) Há và tên: ..................</p>
                                        <p className="signature-line">(7) Sá dinh danh cá nhân:................</p>
                                    </div>
                                    <div className="signature-col">
                                        <p className="signature-line">(7) Há và tên: ..................</p>
                                        <p className="signature-line">(7) Sá dinh danh cá nhân:...............</p>
                                    </div>
                                    <div className="signature-col">
                                        <p className="signature-line">(7) Há và tên: ..................</p>
                                        <p className="signature-line">(7) Sá dinh danh cá nhân:...............</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CT01Modal;